/** 常量定义 */

import { OrderStatus, OrderDisplayStatus, TaskStatus, TaskPriority, PaymentStatus, AlertSeverity, ContainerStatus, NodeStatus, IoTDeviceStatus, WaterSlipStatus, ReceivableDisplayStatus, PaymentNode } from '../types';
import type { NodeProgressStatus } from '../types';
import type { NodeCode, NodeSubstepKey, ProduceLine, SubstepStatus } from '../types/node-substep';

/** 订单状态映射 */
export const ORDER_STATUS_MAP: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: '待确认',
  [OrderStatus.CONFIRMED]: '已确认',
  [OrderStatus.SHIPPING]: '运输中',
  [OrderStatus.EXPORT_CUSTOMS]: '出口报关',
  [OrderStatus.IMPORT_CUSTOMS]: '进口清关',
  [OrderStatus.DELIVERED]: '已交付',
  [OrderStatus.COMPLETED]: '已完成',
  [OrderStatus.CANCELLED]: '已取消',
};

/** 订单状态颜色映射 */
export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: '#FBBC04',
  [OrderStatus.CONFIRMED]: '#1A73E8',
  [OrderStatus.SHIPPING]: '#1A73E8',
  [OrderStatus.EXPORT_CUSTOMS]: '#FBBC04',
  [OrderStatus.IMPORT_CUSTOMS]: '#FBBC04',
  [OrderStatus.DELIVERED]: '#34A853',
  [OrderStatus.COMPLETED]: '#34A853',
  [OrderStatus.CANCELLED]: '#EA4335',
};

/** 容器状态映射 */
export const CONTAINER_STATUS_MAP: Record<ContainerStatus, string> = {
  [ContainerStatus.AT_ORIGIN]: '始发港',
  [ContainerStatus.IN_TRANSIT]: '运输中',
  [ContainerStatus.AT_CUSTOMS]: '海关',
  [ContainerStatus.AT_DESTINATION]: '目的港',
  [ContainerStatus.DELIVERED]: '已交付',
};

/** 容器状态颜色映射 */
export const CONTAINER_STATUS_COLORS: Record<ContainerStatus, string> = {
  [ContainerStatus.AT_ORIGIN]: '#FBBC04',
  [ContainerStatus.IN_TRANSIT]: '#1A73E8',
  [ContainerStatus.AT_CUSTOMS]: '#FBBC04',
  [ContainerStatus.AT_DESTINATION]: '#34A853',
  [ContainerStatus.DELIVERED]: '#34A853',
};

/** 节点状态映射 */
export const NODE_STATUS_MAP: Record<NodeStatus, string> = {
  [NodeStatus.PENDING]: '待处理',
  [NodeStatus.IN_PROGRESS]: '进行中',
  [NodeStatus.COMPLETED]: '已完成',
  [NodeStatus.ALERT]: '异常',
  [NodeStatus.SKIPPED]: '已跳过',
};

/** 节点状态颜色映射 */
export const NODE_STATUS_COLORS: Record<NodeStatus, string> = {
  [NodeStatus.PENDING]: '#9AA0A6',
  [NodeStatus.IN_PROGRESS]: '#1A73E8',
  [NodeStatus.COMPLETED]: '#34A853',
  [NodeStatus.ALERT]: '#EA4335',
  [NodeStatus.SKIPPED]: '#80868B',
};

/** 任务状态映射 */
export const TASK_STATUS_MAP: Record<TaskStatus, string> = {
  [TaskStatus.TODO]: '待处理',
  [TaskStatus.IN_PROGRESS]: '进行中',
  [TaskStatus.COMPLETED]: '已完成',
  [TaskStatus.OVERDUE]: '已逾期',
};

/** 任务优先级映射 */
export const TASK_PRIORITY_MAP: Record<TaskPriority, string> = {
  [TaskPriority.LOW]: '低',
  [TaskPriority.MEDIUM]: '中',
  [TaskPriority.HIGH]: '高',
  [TaskPriority.URGENT]: '紧急',
};

/** 付款状态映射 */
export const PAYMENT_STATUS_MAP: Record<PaymentStatus, string> = {
  [PaymentStatus.UNPAID]: '未付款',
  [PaymentStatus.PARTIALLY_PAID]: '部分付款',
  [PaymentStatus.PAID]: '已付款',
  [PaymentStatus.OVERDUE]: '已逾期',
  [PaymentStatus.DISPUTED]: '争议中',
};

/** 付款状态颜色映射 */
export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  [PaymentStatus.UNPAID]: '#FBBC04',
  [PaymentStatus.PARTIALLY_PAID]: '#1A73E8',
  [PaymentStatus.PAID]: '#34A853',
  [PaymentStatus.OVERDUE]: '#EA4335',
  [PaymentStatus.DISPUTED]: '#EA4335',
};

/** 报警级别映射 */
export const ALERT_SEVERITY_MAP: Record<AlertSeverity, string> = {
  [AlertSeverity.INFO]: '信息',
  [AlertSeverity.WARNING]: '警告',
  [AlertSeverity.CRITICAL]: '严重',
};

/** 报警级别颜色映射 */
export const ALERT_SEVERITY_COLORS: Record<AlertSeverity, string> = {
  [AlertSeverity.INFO]: '#1A73E8',
  [AlertSeverity.WARNING]: '#FBBC04',
  [AlertSeverity.CRITICAL]: '#EA4335',
};

/** IoT设备状态映射 */
export const IOT_DEVICE_STATUS_MAP: Record<IoTDeviceStatus, string> = {
  [IoTDeviceStatus.ONLINE]: '在线',
  [IoTDeviceStatus.OFFLINE]: '离线',
  [IoTDeviceStatus.LOW_BATTERY]: '低电量',
  [IoTDeviceStatus.MALFUNCTION]: '故障',
};

/** 集装箱类型映射 */
export const CONTAINER_TYPE_MAP: Record<string, string> = {
  REEFER_20: '20尺冷藏柜',
  REEFER_40: '40尺冷藏柜',
  DRY_20: '20尺干货柜',
  DRY_40: '40尺干货柜',
};

/** 贸易术语（按 Incoterms 由卖方责任由轻到重排列：EXW 工厂交货 → DDP 完税后交货） */
export const TRADE_TERMS: string[] = ['EXW', 'FOB', 'CFR', 'CIF', 'DAP', 'DDP'];

/**
 * IOR 进口责任人（Importer of Record）：DAP/DDP 门到门术语下进口清关与税费的承担主体。
 * PLATFORM=平台代持（非居民卖家常用）；SELLER=卖方自持；BUYER=买方自行进口；FORWARDER=借货代税号。
 */
export const IOR_TYPES: string[] = ['PLATFORM', 'SELLER', 'BUYER', 'FORWARDER'];

export const IOR_LABELS: Record<string, string> = {
  PLATFORM: '平台代持',
  SELLER: '卖方',
  BUYER: '买方',
  FORWARDER: '货代',
};

/** 需选择 IOR 的贸易术语：DAP/DDP（卖方负责进口清关，必须明确责任人；FOB/CIF 卖方不负责进口） */
export const TRADE_TERMS_WITH_IOR: string[] = ['DAP', 'DDP'];

/** 判断该贸易术语是否需选择 IOR（DAP/DDP 返回 true） */
export function needsIor(tradeTerms?: string | null): boolean {
  return !!tradeTerms && TRADE_TERMS_WITH_IOR.includes(tradeTerms.toUpperCase());
}

/**
 * 贸易术语对应的成本明细字段（对外报出口价按 Incoterms 打包）：
 * EXW=工厂交货，卖方只负责备货，无任何出口环节费用（0 项，成本明细整段不展示）；
 * FOB=基底×数量×(1+利润点%)+国内运输+港杂（2 项，单证费已合并到港杂费，前端不再展示）；
 * CFR=FOB+海运费（3 项）；CIF=CFR+保险费（4 项）；
 * DAP=CIF+清关费+内陆派送费（6 项，门到门但不含进口税——买家自有进口资质时 DDP 反而卖不动）；
 * DDP=DAP+税费（7 项全拆解；税费默认按目的国税率库自动估算，含可配缓冲，多退少补）。
 * 海运费(USD) 在 CFR/CIF/DAP/DDP 时需要填写，其他费用均为 CNY。
 *
 * 注：docFee（单证费）仍在数据模型/DB/API 中保留，用于兼容旧数据，但前台输入/展示已隐藏。
 */
export const TRADE_TERMS_COST_FIELDS: Record<string, string[]> = {
  EXW: [],
  FOB: ['domesticTransport', 'portMiscFee'],
  CFR: ['domesticTransport', 'portMiscFee', 'oceanFreight'],
  CIF: ['domesticTransport', 'portMiscFee', 'insuranceFee', 'oceanFreight'],
  DAP: ['domesticTransport', 'portMiscFee', 'insuranceFee', 'oceanFreight',
        'customsClearanceFee', 'inlandDeliveryFee'],
  DDP: ['domesticTransport', 'portMiscFee', 'insuranceFee', 'oceanFreight',
        'customsClearanceFee', 'taxes', 'inlandDeliveryFee'],
};

/**
 * 全部成本明细字段（用于切换贸易术语时清空「新术语下不可见」的残留值，防止脏值落库/重复计价）。
 * 与 TRADE_TERMS_COST_FIELDS 的 value 集合保持并集一致。
 */
export const ALL_COST_FIELDS: string[] = [
  'domesticTransport', 'portMiscFee', 'docFee', 'insuranceFee', 'oceanFreight',
  'customsClearanceFee', 'taxes', 'inlandDeliveryFee',
];

/**
 * 无需物流/航线信息的贸易术语：EXW（工厂交货）由买方自提并自行安排出口运输，
 * 故建单/报价时隐藏「航线/起运港/目的港/船公司/航行天数/ETD/ETA」并清空其值。
 */
export const TRADE_TERMS_WITHOUT_ROUTE: string[] = ['EXW'];

/** 判断该贸易术语是否需要填写航线/港口/船期等物流信息（EXW 返回 false） */
export function needsRouteInfo(tradeTerms?: string | null): boolean {
  return !!tradeTerms && !TRADE_TERMS_WITHOUT_ROUTE.includes(tradeTerms);
}

/**
 * 展示「航线」（起运港 → 目的港）的贸易术语：CFR/CIF/DDP 卖方承担海运段，航线有业务语义。
 * FOB 卖方在装运港交货即完成义务、海运段由买方安排 → 报价单/分享页展示「交货地」（= 装运港）而非航线。
 * （填写侧仍按 needsRouteInfo 收集港口信息：FOB 的装运港是交货地，目的港是买家自排运输的参考信息。）
 */
export const TRADE_TERMS_WITH_ROUTE: string[] = ['CFR', 'CIF', 'DAP', 'DDP'];

/** 判断该贸易术语是否按「航线」展示（CFR/CIF/DDP 返回 true；FOB/EXW 返回 false） */
export function usesShippingRoute(tradeTerms?: string | null): boolean {
  return !!tradeTerms && TRADE_TERMS_WITH_ROUTE.includes(tradeTerms.toUpperCase());
}

/** 成本字段中文名（纯名称，不含币种；币种+单位由 COST_FIELD_UNIT 提供，避免标签重复括号） */
export const COST_FIELD_LABELS: Record<string, string> = {
  domesticTransport: '国内运输',
  portMiscFee: '港杂费',
  docFee: '单证费',
  insuranceFee: '保险费',
  oceanFreight: '海运费',
  customsClearanceFee: '清关费',
  taxes: '税费',
  inlandDeliveryFee: '目的国内陆派送费',
};

/** 付款方式选项 */
export const PAYMENT_METHODS = [
  { value: 'T/T', label: 'T/T 电汇' },
  { value: 'OA', label: 'OA 赊销' },
  { value: 'BluCredit', label: 'BluCredit' },
  { value: 'L/C', label: 'L/C 信用证' },
  { value: 'D/P', label: 'D/P 付款交单' },
] as const;

/** OA 赊销账期选项（天） */
export const OA_CREDIT_DAYS = [15, 30, 60, 90] as const;

/** 付款比例工具：将输入约束到 [0,100] 并保留 2 位小数（支持 0.5%、33.33% 等） */
export function clampRatio(n: number): number {
  if (n == null || Number.isNaN(n)) return 0;
  const v = Math.min(100, Math.max(0, n));
  return Math.round(v * 100) / 100;
}

/** 保留 2 位小数 */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** 主要港口 */
export const PORTS: string[] = [
  '上海港', '宁波港', '深圳港', '青岛港', '广州港',
  '天津港', '厦门港', '大连港', '连云港', '日照港',
  '洛杉矶港', '长滩港', '鹿特丹港', '汉堡港', '新加坡港',
  '釜山港', '迪拜港', '安特卫普港', '巴生港', '丹戎帕拉帕斯港',
];

/** 产品类目 */
export const PRODUCT_CATEGORIES: string[] = [
  '新鲜水果', '冷冻蔬菜', '谷物', '咖啡豆', '可可豆',
  '茶叶', '坚果', '乳制品', '肉类', '海鲜',
];

/** 10个履约节点名称（code 对齐数据库 node_code 枚举；写法B canonical 节点） */
export const NODE_NAMES: string[] = [
  'order-签约', 'produce-备货', 'pack-包装', 'load-装柜',
  'export_customs-出口报关', 'ship-海运', 'arrive-到港',
  'import_customs-进口清关', 'warehouse-海外仓', 'deliver-配送签收',
];

// ===== D01 柜档案10节点专用常量 =====

/** 10节点短名称（用于进度条展示；与 NODE_NAMES 一一对应） */
export const NODE_SHORT_NAMES: string[] = [
  '签约', '备货', '包装', '装柜', '出口报关', '海运', '到港', '进口清关', '海外仓', '配送签收',
];

/** 节点进度状态颜色映射 */
export const NODE_PROGRESS_COLORS: Record<NodeProgressStatus, string> = {
  done: '#10B981',
  current: '#10B981',
  pending: '#DADCE0',
  alert: '#E53935',
};

// ===== 通用 UI 设计系统色板（V1 严格沿用，前端统一从此处取色，禁止硬编码） =====

/** 通用 UI 配色（主色/渐变/状态/边框/预案块/存证绿） */
export const THEME_COLORS = {
  /** 主色 primary */
  primary: '#10B981',
  /** 主色深（渐变终点） */
  primaryDark: '#059669',
  /** 警告 warning */
  warning: '#FB8C00',
  /** 成功 success */
  success: '#43A047',
  /** 危险 danger */
  danger: '#E53935',
  /** 卡片边框 */
  border: '#F0F2F5',
  /** 待处理灰（步骤条/占比条未达） */
  pending: '#DADCE0',
  /** BluGuard 预案提示块底色 */
  planBg: '#FFF3E0',
  /** BluGuard 预案提示块文字色 */
  planText: '#E65100',
  /** BluGuard 预案提示块边框色 */
  planBorder: '#FFCC80',
  /** 存证声明 banner 底色 */
  verifiedBg: '#E8F5E9',
  /** 存证声明 banner 文字色 */
  verifiedText: '#2E7D32',
} as const;

/** 品类列表 */
export const CONTAINER_CATEGORIES: string[] = [
  '新鲜水果', '冷冻蔬菜', '咖啡豆', '可可豆',
  '茶叶', '坚果', '乳制品', '肉类', '海鲜', '谷物',
];

/** 航线列表 */
export const SHIPPING_ROUTES: string[] = [
  '青岛 → 迪拜', '宁波 → 鹿特丹', '深圳 → 汉堡',
  '青岛 → 新加坡', '上海 → 迪拜', '宁波 → 釜山',
  '深圳 → 洛杉矶', '青岛 → 鹿特丹',
];

// ===== D04 单证一致性校验专用常量 =====

import type { VerificationFieldKey, DocumentType, CompareResult } from '../types/verification';

/** 7 个校验项定义 */
export const VERIFICATION_FIELDS: { key: VerificationFieldKey; name: string }[] = [
  { key: 'weight',           name: '重量(kg)' },
  { key: 'containerNo',      name: '柜号' },
  { key: 'sealNo',           name: '铅封号' },
  { key: 'categoryDesc',     name: '品类描述' },
  { key: 'destinationPort',  name: '目的港' },
  { key: 'shipper',          name: '发货人' },
  { key: 'consignee',        name: '收货人' },
];

/** 单证类型中文标签 */
export const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  CI: 'CI（商业发票）',
  PL: 'PL（装箱单）',
  BL: 'B/L（提单）',
  PHYTO: 'Phyto（植检证）',
};

/** 比对结果中文 + 图标 + 颜色 */
export const COMPARE_RESULT_CONFIG: Record<CompareResult, { label: string; icon: string; color: string }> = {
  match:      { label: '✓ 一致', icon: 'CheckCircle', color: '#43A047' },
  name_diff:  { label: 'ⓘ 译名差异（允许）', icon: 'ErrorOutline', color: '#FB8C00' },
  mismatch:   { label: '✗ 不一致', icon: 'Cancel', color: '#E53935' },
  na:         { label: '—', icon: 'Remove', color: '#9AA0A6' },
};

/** 校验规则说明文本（AlertBanner 使用） */
export const VERIFICATION_RULES_TEXT = `CI.重量 == PL.重量 == B/L.重量 == Phyto.重量 (±5%)；柜号 PL/BL/Phyto 三方严格匹配；铅封号 PL/BL 匹配；品类描述允许译名差异（如"大蒜" ↔ "Garlic"）；目的港/发货人/收货人四方一致。`;

/** 重量容差百分比 */
export const WEIGHT_TOLERANCE_PERCENT = 5;

// ===== C03 商品与类目管理专用常量 =====

import type { DestStandardType } from '../types/product';

/** 目的国标准标签颜色配置 */
export const DEST_STANDARD_COLORS: Record<DestStandardType, { bg: string; text: string }> = {
  phytosanitary: { bg: '#E8F5E9', text: '#2E7D32' },
  food_safety: { bg: '#FFF3E0', text: '#E65100' },
  quality: { bg: '#ECFDF5', text: '#059669' },
};

// ===== C04 交易与订单管理专用常量 =====

/** 订单显示状态中文映射 */
export const ORDER_DISPLAY_STATUS_MAP: Record<OrderDisplayStatus, string> = {
  [OrderDisplayStatus.TEMPERATURE_ALERT]: '温度报警',
  [OrderDisplayStatus.COMPLETED]: '已完成',
  [OrderDisplayStatus.IOT_OFFLINE]: 'IoT离线',
  [OrderDisplayStatus.DOCUMENTS_PENDING]: '四证待校',
  [OrderDisplayStatus.NODE_TIMEOUT]: '节点超时',
  [OrderDisplayStatus.NORMAL]: '正常',
  [OrderDisplayStatus.PENDING_SIGN]: '待签约',
};

/** 订单显示状态颜色映射 */
export const ORDER_DISPLAY_STATUS_COLORS: Record<OrderDisplayStatus, string> = {
  [OrderDisplayStatus.TEMPERATURE_ALERT]: '#E53935',   // Danger Red
  [OrderDisplayStatus.COMPLETED]: '#43A047',            // Success Green
  [OrderDisplayStatus.IOT_OFFLINE]: '#E53935',           // Danger Red
  [OrderDisplayStatus.DOCUMENTS_PENDING]: '#FB8C00',     // Warning Orange
  [OrderDisplayStatus.NODE_TIMEOUT]: '#FB8C00',          // Warning Orange
  [OrderDisplayStatus.NORMAL]: '#10B981',                // Primary Blue
  [OrderDisplayStatus.PENDING_SIGN]: '#9E9E9E',          // Grey
};

/** Danger 行状态对应的 displayStatus */
export const ROW_DANGER_STATUSES: OrderDisplayStatus[] = [
  OrderDisplayStatus.TEMPERATURE_ALERT,
  OrderDisplayStatus.IOT_OFFLINE,
];

/** Warning 行状态对应的 displayStatus */
export const ROW_WARNING_STATUSES: OrderDisplayStatus[] = [
  OrderDisplayStatus.NODE_TIMEOUT,
  OrderDisplayStatus.DOCUMENTS_PENDING,
];

/** 节点颜色预设 */
export const NODE_COLORS: Record<string, string> = {
  normal: '#43A047',   // 绿色 — 正常进行中
  warning: '#FB8C00',  // 橙色 — 接近 SLA 超时
  alert: '#E53935',    // 红色 — 已超时或报警
};

// ===== C05 财务与收款管理专用常量 =====

/** 应收显示状态中文映射 */
export const RECEIVABLE_DISPLAY_STATUS_MAP: Record<ReceivableDisplayStatus, string> = {
  [ReceivableDisplayStatus.OVERDUE_7D]: '逾期7天',
  [ReceivableDisplayStatus.OVERDUE_3D]: '逾期3天',
  [ReceivableDisplayStatus.DUE_SOON]: '即将到期',
  [ReceivableDisplayStatus.NORMAL]: '正常',
  [ReceivableDisplayStatus.COMPLETED]: '已完成',
};

/** 应收显示状态颜色映射 */
export const RECEIVABLE_DISPLAY_STATUS_COLORS: Record<ReceivableDisplayStatus, string> = {
  [ReceivableDisplayStatus.OVERDUE_7D]: '#E53935',   // Danger Red
  [ReceivableDisplayStatus.OVERDUE_3D]: '#FB8C00',   // Warning Orange
  [ReceivableDisplayStatus.DUE_SOON]: '#FB8C00',     // Warning Orange
  [ReceivableDisplayStatus.NORMAL]: '#10B981',       // Primary Blue
  [ReceivableDisplayStatus.COMPLETED]: '#43A047',    // Success Green
};

/** 水单状态中文映射 */
export const WATER_SLIP_STATUS_MAP: Record<WaterSlipStatus, string> = {
  [WaterSlipStatus.VERIFIED]: '已核销',
  [WaterSlipStatus.PENDING]: '待核销',
  [WaterSlipStatus.UNSUBMITTED]: '未提交',
};

/** 水单状态颜色映射 */
export const WATER_SLIP_STATUS_COLORS: Record<WaterSlipStatus, string> = {
  [WaterSlipStatus.VERIFIED]: '#43A047',    // Success Green
  [WaterSlipStatus.PENDING]: '#FB8C00',     // Warning Orange
  [WaterSlipStatus.UNSUBMITTED]: '#9E9E9E', // Grey
};

/** 付款节点中文映射 */
export const PAYMENT_NODE_LABEL_MAP: Record<PaymentNode, string> = {
  [PaymentNode.T_T_DEPOSIT]: 'T/T 30%定金',
  [PaymentNode.T_T_BALANCE]: 'T/T 70%尾款',
  [PaymentNode.FULLY_PAID]: '全额已付',
  [PaymentNode.L_C]: 'L/C',
  [PaymentNode.BLU_CREDIT]: 'BluCredit',
};

/** 收款计划状态映射 */
export const PLAN_STATUS_MAP: Record<string, string> = {
  pending: '待收款',
  completed: '已完成',
  overdue: '已逾期',
};

/** 收款计划状态颜色映射 */
export const PLAN_STATUS_COLORS: Record<string, string> = {
  pending: '#FB8C00',
  completed: '#43A047',
  overdue: '#E53935',
};

/** Danger 行状态对应的 displayStatus（逾期） */
export const ROW_DANGER_RECEIVABLE_STATUSES: ReceivableDisplayStatus[] = [
  ReceivableDisplayStatus.OVERDUE_7D,
  ReceivableDisplayStatus.OVERDUE_3D,
];

/** Warning 行状态对应的 displayStatus（即将到期） */
export const ROW_WARNING_RECEIVABLE_STATUSES: ReceivableDisplayStatus[] = [
  ReceivableDisplayStatus.DUE_SOON,
];

/** 币种选项（筛选下拉） */
export const CURRENCY_OPTIONS: { label: string; value: string }[] = [
  { label: 'USD', value: 'USD' },
  { label: 'CNY', value: 'CNY' },
  { label: 'AED', value: 'AED' },
];

// ===== D03 报关与清关管理专用常量 =====

import type {
  InspectionStatus,
  PhytoStatus,
  DeclarationSubmitStatus,
  ReleaseStatus,
  DocumentStatus,
  ClearanceStepStatus,
} from '../types';

/** 商检状态标签 */
export const INSPECTION_STATUS_LABELS: Record<InspectionStatus, string> = {
  PASSED: '已通过',
  PENDING: '待检验',
  FAILED: '未通过',
};

/** 商检状态颜色 */
export const INSPECTION_STATUS_COLORS: Record<InspectionStatus, string> = {
  PASSED: '#43A047',
  PENDING: '#FB8C00',
  FAILED: '#E53935',
};

/** 植检状态标签 */
export const PHYTO_STATUS_LABELS: Record<PhytoStatus, string> = {
  PASSED: '已通过',
  PENDING: '待签发',
  FAILED: '未通过',
};

/** 植检状态颜色 */
export const PHYTO_STATUS_COLORS: Record<PhytoStatus, string> = {
  PASSED: '#43A047',
  PENDING: '#FB8C00',
  FAILED: '#E53935',
};

/** 海关申报状态标签 */
export const DECLARATION_SUBMIT_LABELS: Record<DeclarationSubmitStatus, string> = {
  SUBMITTED: '已申报',
  UNDER_REVIEW: '审核中',
  APPROVED: '已批准',
  REJECTED: '已退回',
  NOT_SUBMITTED: '未提交',
};

/** 海关申报状态颜色 */
export const DECLARATION_SUBMIT_COLORS: Record<DeclarationSubmitStatus, string> = {
  SUBMITTED: '#10B981',
  UNDER_REVIEW: '#FB8C00',
  APPROVED: '#43A047',
  REJECTED: '#E53935',
  NOT_SUBMITTED: '#9E9E9E',
};

/** 放行状态标签 */
export const RELEASE_STATUS_LABELS: Record<ReleaseStatus, string> = {
  RELEASED: '已放行',
  PENDING_RELEASE: '待放行',
  HELD: '扣留',
};

/** 放行状态颜色 */
export const RELEASE_STATUS_COLORS: Record<ReleaseStatus, string> = {
  RELEASED: '#43A047',
  PENDING_RELEASE: '#FB8C00',
  HELD: '#E53935',
};

/** 资料状态 → 图标 + 颜色映射 */
export const DOCUMENT_STATUS_ICONS: Record<DocumentStatus, { icon: string; color: string }> = {
  READY: { icon: '✓', color: '#43A047' },
  PENDING: { icon: '⏳', color: '#FB8C00' },
  MISSING: { icon: '✗', color: '#E53935' },
};

/** 资料状态标签 */
export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  READY: '已上传',
  PENDING: '待上传',
  MISSING: '缺失',
};

/** 6 步清关流程名称 */
export const CLEARANCE_STEP_NAMES: string[] = [
  '到港', 'MOCCAE 申报', 'Municipality 申报', '关税缴纳', '海关清关', '提货/配送',
];

/** 迪拜双检验流程说明 */
export const DUBAI_INSPECTION_ALERT: string =
  '迪拜进口实施 MOCCAE（气候变化与环境部）+ Municipality（市政局）双机构检验，两机构检验均通过后方可清关提货。';

/** 清关步骤状态 → Timeline 状态映射 */
export const STEP_STATUS_MAP: Record<ClearanceStepStatus, 'done' | 'warning' | undefined> = {
  DONE: 'done',
  CURRENT: 'warning',
  PENDING: undefined,
};

// ===== D05 IoT 设备与报警中心专用常量 =====

/** 温度阈值（摄氏度） */
export const TEMP_THRESHOLD = {
  /** 报警上限 */
  UPPER: 4,
  /** 报警下限 */
  LOWER: 0,
  /** 目标温度 */
  TARGET: -3,
} as const;

/** 电量预警阈值（< 20% 触发预警） */
export const BATTERY_WARNING_THRESHOLD = 20;

/**
 * 时间范围选项（装柜时间锚定）。
 * full 档 hours=0 表示无固定小时数——窗口由调用方按「装柜时间 → 交付时间/现在」动态计算。
 */
export const TIME_RANGE_OPTIONS: { value: import('../types').TimeRange; label: string; hours: number }[] = [
  { value: '24h', label: '24h', hours: 24 },
  { value: '3d', label: '3天', hours: 72 },
  { value: '7d', label: '7天', hours: 168 },
  { value: 'full', label: '全程', hours: 0 },
];

/** IoT 设备状态颜色映射 */
export const IOT_DEVICE_STATUS_COLORS: Record<IoTDeviceStatus, string> = {
  [IoTDeviceStatus.ONLINE]: '#43A047',
  [IoTDeviceStatus.OFFLINE]: '#9AA0A6',
  [IoTDeviceStatus.LOW_BATTERY]: '#FB8C00',
  [IoTDeviceStatus.MALFUNCTION]: '#E53935',
};

/** Danger 行状态对应的设备状态（MALFUNCTION） */
export const DEVICE_DANGER_STATUSES: IoTDeviceStatus[] = [
  IoTDeviceStatus.MALFUNCTION,
];

/** Warning 行状态对应的设备状态（LOW_BATTERY + OFFLINE） */
export const DEVICE_WARNING_STATUSES: IoTDeviceStatus[] = [
  IoTDeviceStatus.LOW_BATTERY,
  IoTDeviceStatus.OFFLINE,
];

// ===== D07 BluGuard 理赔规则引擎专用常量 =====

import type {
  ClaimType,
  LiabilityResult,
  ClaimWorkOrderStatus,
  LiabilitySegment,
  ClaimRuleConfigData,
  ClaimLogAction,
  ClaimRejectReason,
} from '../types';

/** 理赔类型中文映射 */
export const CLAIM_TYPE_MAP: Record<ClaimType, string> = {
  temperature_exceeded: '温度超限',
  iot_offline: 'IoT离线',
  cargo_damage: '货损',
  shortage: '短装',
};

/** 理赔类型四色映射 */
export const CLAIM_TYPE_COLORS: Record<ClaimType, string> = {
  temperature_exceeded: '#E53935',
  iot_offline: '#9AA0A6',
  cargo_damage: '#FB8C00',
  shortage: '#10B981',
};

/**
 * 定责结果中文映射。
 * <p>历史：原本是 PG 枚举的 5 个 code（assessing/pending_liability/carrier_60/
 * platform_100/pre_loading_40），固定比例下走"船公司60%/平台100%/装柜前40%"等
 * 硬编码文案。后续按"灵活定责"拍板改为 varchar(32)，写入的是
 * {@code claim_liability_segments.label}（装柜前/海运段/目的港），
 * 故扩展为开放字典 + 兜底 '—'。
 * <p>key 兼容旧 code + 新 label；旧 code 继续保留用于兼容历史数据。
 */
export const LIABILITY_RESULT_MAP: Record<string, string> = {
  // 系统态（未走人工定责）
  assessing: '定损中',
  pending_liability: '待定责',
  // 历史固定比例 code（兼容）
  carrier_60: '船公司60%',
  platform_100: '平台100%',
  pre_loading_40: '装柜前40%',
  // 当前人工定责写入的 segment label（自由比例，故文案不带数字）
  装柜前: '装柜前',
  海运段: '船公司（海运段）',
  目的港: '平台（目的港）',
};

/** 定责结果颜色映射（同 key 集合与 MAP 对齐） */
export const LIABILITY_RESULT_COLORS: Record<string, string> = {
  assessing: '#9E9E9E',
  pending_liability: '#9E9E9E',
  carrier_60: '#FB8C00',
  platform_100: '#43A047',
  pre_loading_40: '#10B981',
  装柜前: '#FB8C00',
  海运段: '#10B981',
  目的港: '#43A047',
};

/** MAP 查询兜底：未知 code/label 也展示出原文，避免空 chip */
export function lookupLiabilityResult(value: string | null | undefined): string {
  if (!value) return '—';
  return LIABILITY_RESULT_MAP[value] ?? value;
}

/** 颜色查询兜底 */
export function lookupLiabilityResultColor(value: string | null | undefined): string {
  if (!value) return '#9E9E9E';
  return LIABILITY_RESULT_COLORS[value] ?? '#9E9E9E';
}

/** 工单状态中文映射 */
export const CLAIM_WORK_ORDER_STATUS_MAP: Record<ClaimWorkOrderStatus, string> = {
  processing: '处理中',
  assessing: '定损中',
  approving: '审批中',
  pending_payout: '待赔付',
  settled_paid: '已结案-赔付',
  settled_rejected: '已结案-拒赔',
};

/** 工单状态颜色映射 */
export const CLAIM_WORK_ORDER_STATUS_COLORS: Record<ClaimWorkOrderStatus, string> = {
  processing: '#10B981',
  assessing: '#FB8C00',
  approving: '#FB8C00',
  pending_payout: '#10B981',
  settled_paid: '#43A047',
  settled_rejected: '#E53935',
};

/** Warning 行状态对应的工单状态（处理中/定损中/审批中 → 橙色行背景） */
export const ROW_WARNING_CLAIM_STATUSES: ClaimWorkOrderStatus[] = [
  'processing',
  'assessing',
  'approving',
];

// ===== 理赔详情页增强：流转日志 / 驳回原因 / 曲线窗口 =====

/**
 * 流转日志动作中文映射（**兜底**用）。
 *
 * <p>`ADVANCE` 仍保留「推进状态」作为最终兜底，但正常渲染路径应优先用
 * `deriveActionLabel()` 按 `fromStatus → toStatus` 派生更具体的动作语义
 * （如「提交定损」「审批通过」），避免时间轴上出现连续 4 条同名「推进状态」。
 */
export const CLAIM_LOG_ACTION_MAP: Record<ClaimLogAction, string> = {
  CREATE: '建单',
  ADVANCE: '推进状态',
  REJECT: '驳回',
  ROLLBACK: '打回',
  AMOUNT_UPDATE: '登记赔付金额',
  LIABILITY_UPDATE: '定责变更',
  FIELD_UPDATE: '编辑信息',
};

/** 流转日志圆点状态映射（驳回红色 danger，打回橙色 warning） */
export const CLAIM_LOG_STATUS_MAP: Record<ClaimLogAction, 'done' | 'danger' | 'warning'> = {
  CREATE: 'done',
  ADVANCE: 'done',
  AMOUNT_UPDATE: 'done',
  LIABILITY_UPDATE: 'done',
  FIELD_UPDATE: 'done',
  REJECT: 'danger',
  ROLLBACK: 'warning',
};

/**
 * 流转日志动作配色（时间轴圆点色）。
 *
 * <p>取自平台主题四色：Primary #10B981 / Success #43A047 / Danger #E53935 / Warning #FB8C00。
 * 语义：建单蓝、正向推进绿、驳回红、打回与各类修改橙。
 */
export const CLAIM_LOG_ACTION_COLORS: Record<ClaimLogAction, string> = {
  CREATE: '#10B981',
  ADVANCE: '#43A047',
  REJECT: '#E53935',
  ROLLBACK: '#FB8C00',
  AMOUNT_UPDATE: '#FB8C00',
  LIABILITY_UPDATE: '#FB8C00',
  FIELD_UPDATE: '#FB8C00',
};

/**
 * 推进动作的语义标签表：键为 `${fromStatus}->${toStatus}`。
 *
 * <p>与后端 `ClaimStatusMachine.NEXT` 常量表一一对应；缺键时由
 * `deriveActionLabel()` 回落到「状态变更」+ 中文状态对。
 */
export const CLAIM_ADVANCE_LABEL_MAP: Record<string, string> = {
  'processing->assessing': '提交定损',
  'assessing->approving': '提交审批',
  'approving->pending_payout': '审批通过',
  'pending_payout->settled_paid': '确认赔付',
};

/**
 * 打回动作的语义标签表：键为 `${fromStatus}->${toStatus}`。
 *
 * <p>与后端 `ClaimStatusMachine.ROLLBACK_TARGET` 常量表一一对应。
 */
export const CLAIM_ROLLBACK_LABEL_MAP: Record<string, string> = {
  'assessing->processing': '打回处理中',
  'approving->assessing': '打回定损中',
  'pending_payout->approving': '打回审批中',
};

/**
 * 允许「打回上一态」的状态集合（与后端 `ClaimStatusMachine.canRollback` 一致）。
 *
 * <p>首态 `processing` 无上一态、两个终态不可逆，均不在此列表内。
 */
export const CLAIM_ROLLBACKABLE_STATUSES: ClaimWorkOrderStatus[] = [
  'assessing',
  'approving',
  'pending_payout',
];

/** 驳回原因中文映射（键与后端 ClaimRejectDTO.REASON_CODES 严格一致） */
export const CLAIM_REJECT_REASON_MAP: Record<ClaimRejectReason, string> = {
  insufficient_evidence: '证据不足',
  not_carrier_liability: '非承运方责任',
  duplicate_report: '重复报案',
  customer_withdrawn: '客户撤案',
  out_of_coverage: '超出保障范围',
  other: '其他',
};

/** 驳回原因下拉选项（顺序稳定，与产品拍板顺序一致） */
export const CLAIM_REJECT_REASON_OPTIONS: Array<{ value: ClaimRejectReason; label: string }> = [
  { value: 'insufficient_evidence', label: '证据不足' },
  { value: 'not_carrier_liability', label: '非承运方责任' },
  { value: 'duplicate_report', label: '重复报案' },
  { value: 'customer_withdrawn', label: '客户撤案' },
  { value: 'out_of_coverage', label: '超出保障范围' },
  { value: 'other', label: '其他' },
];

/** 需要强制填写补充说明的驳回原因 */
export const CLAIM_REJECT_REASON_REQUIRING_REMARK: ClaimRejectReason = 'other';

/** 备注 / 补充说明最大长度（与后端 REMARK_MAX_LENGTH 一致） */
export const CLAIM_REMARK_MAX_LENGTH = 200;

/**
 * 「全程」档哨兵值（分钟）——语义为「装柜 → 交付(或现在)」全窗口。
 *
 * 注意（需后端配合）：后端 claim temp-curve 当前只接受 windowMinutes
 * （blu-trade `GET /claim/{id}/temp-curve` → blu-iot `claim-context`，
 * 且 blu-iot 侧夹紧到 [1, 240] 分钟）。在后端支持全窗口语义
 * （如 windowMode=full 或显式起止参数）之前，本档实际拿到的是
 * 后端上限 240 分钟（±4h）的窗口，前端在界面上已如实标注，不假装已支持。
 */
export const CLAIM_CURVE_FULL_WINDOW_MINUTES = 999999;

/** 温度曲线时间窗档位（默认 60，即告警时刻 ±1h） */
export const CLAIM_CURVE_WINDOW_OPTIONS = [
  { value: 30, label: '±30min' },
  { value: 60, label: '±1h' },
  { value: 120, label: '±2h' },
  { value: CLAIM_CURVE_FULL_WINDOW_MINUTES, label: '全程' },
] as const;

/** 温度曲线默认时间窗（分钟） */
export const CLAIM_CURVE_DEFAULT_WINDOW = 60;

/** IoT 定责三段默认配置 */
export const DEFAULT_LIABILITY_SEGMENTS: LiabilitySegment[] = [
  { label: '装柜前段 (产地责任)', percentage: 40, isCurrent: false, description: '装柜前环节责任', color: '#FB8C00' },
  { label: '海运段 (船公司责任)', percentage: 45, isCurrent: true, description: '船公司承担的货损责任', color: '#10B981' },
  { label: '目的港（买家责任）', percentage: 15, isCurrent: false, description: '买家承担的货损责任', color: '#43A047' },
];

/** 默认规则配置 */
export const DEFAULT_CLAIM_RULE_CONFIG: ClaimRuleConfigData = {
  tempUpperThreshold: 4,
  durationHours: 4,
  deductible: 500,
  payoutRatio: 85,
  iotLiabilitySegments: DEFAULT_LIABILITY_SEGMENTS,
};

// ===== D08 风控与买家评级专用常量 =====

import type { CreditRating, CreditStatus, InterceptReason } from '../types';

/** A/B/C 评级中文映射 */
export const CREDIT_RATING_LABELS: Record<CreditRating, string> = {
  A: 'A级',
  B: 'B级',
  C: 'C级',
};

/** A/B/C 评级颜色（badge 用） */
export const CREDIT_RATING_COLORS: Record<CreditRating, string> = {
  A: '#43A047',   // Success Green — 信用良好
  B: '#FB8C00',   // Warning Orange — 需观察
  C: '#E53935',   // Danger Red — 高风险
};

/** 授信状态中文映射 */
export const CREDIT_STATUS_LABELS: Record<CreditStatus, string> = {
  active: '活跃',
  watch: '观察',
  frozen: '冻结',
};

/** 授信状态颜色映射 */
export const CREDIT_STATUS_COLORS: Record<CreditStatus, string> = {
  active: '#43A047',   // Success Green
  watch: '#FB8C00',    // Warning Orange
  frozen: '#E53935',   // Danger Red
};

/** 拦截原因中文映射 */
export const INTERCEPT_REASON_LABELS: Record<InterceptReason, string> = {
  insufficient_credit: '额度不足',
  overdue: '逾期',
};

/** 拦截原因颜色映射 */
export const INTERCEPT_REASON_COLORS: Record<InterceptReason, string> = {
  insufficient_credit: '#FB8C00',   // Warning Orange
  overdue: '#E53935',               // Danger Red
};

/** Danger 行状态对应的 credit status（C级冻结 → #FFEBEE） */
export const ROW_DANGER_CREDIT_STATUSES: CreditStatus[] = ['frozen'];

/** Warning 行状态对应的 credit status（B级观察 → #FFF3E0） */
export const ROW_WARNING_CREDIT_STATUSES: CreditStatus[] = ['watch'];

// ===== G04 系统配置与数据字典专用常量 =====

import type { ControlPointLevel, TemplateStatus } from '../types';

/** CP 控制点颜色映射 */
export const CONTROL_POINT_COLORS: Record<ControlPointLevel | 'NONE', { bg: string; text: string }> = {
  'CP-1': { bg: '#FFEBEE', text: '#C62828' },
  'CP-2': { bg: '#FFF3E0', text: '#E65100' },
  '':     { bg: 'transparent', text: '#616161' },
  'NONE': { bg: 'transparent', text: '#616161' },
};

/** 阈值级别颜色映射 */
export const THRESHOLD_LEVEL_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  target:  { bg: '#FFEBEE', border: '#EF9A9A', text: '#C62828' },
  warning: { bg: '#FFF3E0', border: '#FFCC80', text: '#E65100' },
  alarm:   { bg: '#FFEBEE', border: '#E53935', text: '#C62828' },
};

/** 模板状态颜色映射 */
export const TEMPLATE_STATUS_COLORS: Record<TemplateStatus, { bg: string; text: string }> = {
  configured: { bg: '#E8F5E9', text: '#2E7D32' },
  pending:    { bg: '#FFF3E0', text: '#E65100' },
};

/** 模板状态中文标签 */
export const TEMPLATE_STATUS_LABELS: Record<TemplateStatus, string> = {
  configured: '已配置',
  pending:    '待更新',
};

// ===== G05 RBAC 与审计日志专用常量 =====

/** 角色状态中文映射 */
export const ROLE_STATUS_MAP: Record<string, string> = {
  active: '活跃',
  inactive: '停用',
};

/** 角色状态颜色映射 */
export const ROLE_STATUS_COLORS: Record<string, string> = {
  active: '#43A047',
  inactive: '#9E9E9E',
};

/** 审计结果中文映射 */
export const AUDIT_RESULT_MAP: Record<string, string> = {
  success: '成功',
  failure: '失败',
};

/** 审计结果颜色映射 */
export const AUDIT_RESULT_COLORS: Record<string, string> = {
  success: '#43A047',
  failure: '#E53935',
};

/** 风险等级中文映射 */
export const RISK_LEVEL_MAP: Record<string, string> = {
  high: '高',
  critical: '严重',
};

/** 风险等级颜色映射 */
export const RISK_LEVEL_COLORS: Record<string, string> = {
  high: '#FB8C00',
  critical: '#E53935',
};

/** 审计操作类型列表 */
export const AUDIT_ACTION_TYPES: string[] = [
  '登录', '登出', '创建订单', '修改订单', '删除',
  '导出报表', '修改配置', '审核', '分配任务', '绑定设备', '角色变更',
];

/** 敏感操作名称列表 */
export const SENSITIVE_OP_NAMES: string[] = [
  '节点冲正', '手动核销', '额度调整', '阈值修改', '理赔审批',
  '权限变更', '数据删除', '配置回滚',
];

/** 8 个角色 ID 列表 */
export const G05_ROLE_IDS: string[] = ['R001', 'R002', 'R003', 'R004', 'R005', 'R006', 'R007', 'R008'];

/** 8 个角色名称列表 */
export const G05_ROLE_NAMES: string[] = [
  '超级管理员', '运营经理', '产地组长', '船务专员',
  '质检员', '财务专员', '迪拜团队', 'IoT管理员',
];

/** 所有权限列表 */
export const ALL_PERMISSIONS: string[] = [
  'order:view', 'order:create', 'order:edit', 'order:delete',
  'container:view', 'container:edit',
  'shipping:view', 'shipping:manage',
  'customs:view', 'customs:manage',
  'finance:view', 'finance:edit', 'finance:approve',
  'iot:view', 'iot:manage', 'iot:bind',
  'alert:view', 'alert:manage',
  'report:view', 'report:export',
  'system:config_view', 'system:config_edit',
  'buyer:view',
  'task:view', 'task:assign', 'task:execute',
];

// ===== G01 AI Copilot 与公共验真专用常量 =====

import { ChatMessageType, AICapabilityStatus, HITLPriority } from '../types';

/** 对话消息类型中文标签 */
export const CHAT_MESSAGE_TYPE_LABELS: Record<ChatMessageType, string> = {
  [ChatMessageType.MORNING_REPORT]: '📊 AI 早报',
  [ChatMessageType.USER_REQUEST]: '用户请求',
  [ChatMessageType.EVIDENCE_REPORT]: '📋 证据报告',
  [ChatMessageType.SYSTEM_WARNING]: '⚠ 系统警告',
};

/** AI 能力状态中英文映射 */
export const AI_CAPABILITY_STATUS_MAP: Record<AICapabilityStatus, string> = {
  [AICapabilityStatus.ACTIVE]: '已上线',
  [AICapabilityStatus.DEVELOPING]: '开发中',
  [AICapabilityStatus.PLANNED]: '规划中',
};

/** AI 能力状态颜色映射 */
export const AI_CAPABILITY_STATUS_COLORS: Record<AICapabilityStatus, { bg: string; text: string }> = {
  [AICapabilityStatus.ACTIVE]: { bg: '#E8F5E9', text: '#2E7D32' },
  [AICapabilityStatus.DEVELOPING]: { bg: '#FFF3E0', text: '#E65100' },
  [AICapabilityStatus.PLANNED]: { bg: '#F5F5F5', text: '#9E9E9E' },
};

/** HITL 优先级颜色映射 */
export const HITL_PRIORITY_COLORS: Record<HITLPriority, string> = {
  [HITLPriority.HIGH]: '#FB8C00',
  [HITLPriority.CRITICAL]: '#E53935',
};

/** HITL 优先级中文标签 */
export const HITL_PRIORITY_LABELS: Record<HITLPriority, string> = {
  [HITLPriority.HIGH]: '高',
  [HITLPriority.CRITICAL]: '严重',
};

/** AI 能力图标映射（emoji 到 MUI icon 名称的对应） */
export const AI_CAPABILITY_ICONS: Record<string, string> = {
  account_tree: '🌳',
  gavel: '⚖️',
  device_thermostat: '🌡️',
  schedule: '📅',
  trending_up: '📈',
  travel_explore: '🔍',
};

/** Token 过期预警阈值（小时） */
export const TOKEN_EXPIRY_WARNING_HOURS = 48;

/** 打字动画默认速度（毫秒/字符） */
export const TYPING_ANIMATION_SPEED_MS = 30;

/** 模拟 AI 响应延迟范围（毫秒） */
export const AI_RESPONSE_DELAY_MIN_MS = 1200;
export const AI_RESPONSE_DELAY_MAX_MS = 2500;

// ===== D01 柜档案「双线并行 + 子步骤」专用常量 =====

/**
 * N2 双线定义：货物线(goods_prep) + 物流线(booking)。
 * 两条线状态独立推进、互不影响。
 */
export const PRODUCE_DUAL_LINES: { key: NodeSubstepKey; line: ProduceLine; label: string }[] = [
  { key: 'goods_prep', line: 'goods', label: '货物线（备货）' },
  { key: 'booking', line: 'logistics', label: '物流线（订舱）' },
];

/**
 * N4 三步串行子步骤（顺序固定，不可乱序；与后端 node_substeps 配置一致）：
 * 装货(loading) → 封柜+IoT(seal_iot) → 运输车辆信息(transport)。
 * 前一步未完成则其下一步禁用。
 *
 * ⚠️ 2026-08-08 装柜节点子步骤灵活配置：pickup_empty 已退役；
 * 建柜档案入口改为 archive_create（ACTION_ENTRY，不在此顺序列表内）。
 * 该常量仅作 mock / 离线 fallback 排序；线上渲染由 node_substeps 配置驱动。
 */
export const LOAD_SUBSTEPS: { key: NodeSubstepKey; label: string }[] = [
  { key: 'loading', label: '装货' },
  { key: 'seal_iot', label: '封柜 + IoT 温控' },
  { key: 'transport', label: '运输车辆信息' },
];

/**
 * 子步骤按节点映射：仅 produce(双线) 与 load(三步) 拥有子步骤，
 * 其他节点无子步骤可视化。key 为 NodeCode。
 */
export const SUBSTEPS_BY_NODE: Partial<Record<NodeCode, NodeSubstepKey[]>> = {
  produce: ['goods_prep', 'booking'],
  load: ['loading', 'seal_iot', 'transport'],
};

/**
 * 子步骤状态颜色 —— 与 NODE_PROGRESS_COLORS / THEME_COLORS 同源取色（禁止硬编码新色值）。
 *  pending 灰 #DADCE0 / in_progress 蓝 #10B981 / completed 绿 #43A047 / failed 红 #E53935
 */
export const SUBSTEP_STATUS_COLORS: Record<SubstepStatus, string> = {
  pending: '#DADCE0',
  in_progress: '#10B981',
  completed: '#43A047',
  failed: '#E53935',
};

/** 货物线(goods_prep) 默认 5 个备货细项 */
export const GOODS_PREP_CHECKLIST: string[] = [
  '采收', '分级', '加工', '预冷', '仓储待运',
];
